'use strict';
/* ==========================================================================
   levels/court.js — step "court" (orientation any): YOU BE THE JUSTICE (DESIGN.md §5.3). Four real Supreme Court cases.

   An attempt = 4 cases from content/bank.court.json, drawn by the model (Rights.attempt('court', json, {fresh}): at
   least 2 on the Florida Civics EOC list; each case's year and vote come from Rights.CASES, its two questions are
   shuffled bank instances). Nothing here types in a year, a vote or an answer.

   The room: the Court building cut-out at the back, a red drape, nine empty chairs behind the bench, the gavel.
   Per case:  docket (the docket sheet slides in: the case name sealed under a paper flap "CASE No. n", the kid-safe
              facts in typewriter)
           →  ruling: "How did the Court rule?" (3 options); a wrong pick is crossed out with the item's why.ruling
           →  idea:   "Which idea decided it?"   (3 options); a wrong pick is crossed out with the item's why.idea
           →  reveal: the flap tears off (the case name), the wax seal stamps the real year over the vote, the gavel
              raps, the holding writes on, "ON THE CIVICS EOC" when the case is on the list → next case.
   Stars: ruling right first try 1 + idea right first try 1; of 8, pass = bank.court.json passStars (5).
   Game.progress.scores.court = {best, last, of}; first answers under Game.progress.answers['court/<id>/ruling' | '/idea'].
   A pass → "Collect your stamp" (Game.complete); leaving a passed attempt without collecting still keeps the stamp.
   Attempt + seed: Game.progress.bank.court = {pool, seed, attempt, ids, prev, open, run: {i, ev, res}}: a reload
   mid-attempt resumes the same case with the same picks (?seed= / window.__bankSeed force a seed).

   Portrait phone (Dock.active): the courtroom + the docket sheet are the picture; the facts, the options, the
   holding, the stars and the buttons are dock widgets.
   Keys: 1–3 = option, Enter / Space = start / next.

   QA hooks: sc.phase ('intro' | 'docket' | 'ruling' | 'idea' | 'reveal' | 'end'), sc.case (index 0–3), sc.cur (the
   case instance), sc.cases, sc.seed, sc.attempt, sc.resumed, sc.results, sc.stars, sc.picks {ruling: [], idea: []},
   sc.answer() / sc.solution ({ruling, idea}: the right option indices), sc.begin(fresh), sc.start(), sc.choose(i),
   sc.next(), sc.collect(), sc.done(), sc.finish() (= done: closes the attempt and stamps the step; integration.js);
   Levels.court.solution (the live scene's), Levels.court.preview(ids) (swap the attempt's cases for these item ids:
   screenshots of the longest facts and holdings).
   ========================================================================== */
(function () {
  const STEP = 'court', TITLE = 'You Be the Justice';
  const SLIDE = 0.6, NEXT_GUARD = 0.3;
  const CV = () => window.Civic;
  const C = () => CV().C;
  const json = () => Game.content.bank_court || { items: [], draw: {} };
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", TYPE = "'Special Elite'", HAND = "'Caveat Brush'";
  const INK = '#2b2420', PENCIL = '#5b6475', WOOD = '#6b4428', WOOD_HI = '#8a5a34', LEATHER = '#4a1f1c', DRAPE = '#8e2a24', MARBLE = '#e2dac6';
  const BOTTOM = 1066;
  const sfx = (name, alt, o) => { const n = Audio.has(name) ? name : alt; if (n) Audio.play(n, o); };
  const dash = v => String(v).replace('-', '–');

  /* ---------------- text helpers ---------------- */
  const measure = (s, font) => { g.save(); g.font = font; const w = g.measureText(s).width; g.restore(); return w; };
  const wrap = (t, w, font) => UI._wrap(String(t), w, font);
  function text(lines, x, yTop, lh, font, color, align = 'left') {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = align; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  /* below the HUD: the ⌂ slot and the subtitle line (collage_hud writes it at y 150 + (fs − 44) / 2, fs = UI.text(44)) */
  const minTop = () => { const hs = UI.hudSlot(0), fs = UI.text(44); return Math.max(96, hs.y + hs.r + 18, 150 + (fs - 44) * 0.5 + fs * 0.5 + 14); };

  /* ---------------- the docket sheet's layout ----------------
     X, W: the sheet's left edge and width; sizes shrink until it fits between top0 (preferred) / minTop and BOTTOM. */
  function layoutSheet(k, { X, W, top0, big }) {
    const r = big ? 100 : 72, left = 64, textW = W - left - 44;
    const nameW = W - left - 30 - r * 2.5;
    const base = big ? { f: 54, h: 58, n: 70, l: 30 } : { f: Math.round(UI.text(32)), h: Math.round(UI.text(38)), n: Math.round(UI.text(50)), l: Math.round(UI.text(22)) };
    const lim = BOTTOM - minTop();
    let L = null;
    for (let step = 0; step < 14; step++) {
      const f = Math.max(big ? 40 : 22, base.f - step * 2), hs = Math.max(big ? 44 : 26, base.h - step * 2), lbl = Math.max(18, base.l - Math.floor(step / 2));
      // the case name: one line (shrinking), else two lines
      let ns = Math.max(30, base.n - step * 2), nameL = [k.name];
      while (ns > 34 && measure(k.name, `${ns}px ${SLAB}`) > nameW) ns -= 2;
      if (measure(k.name, `${ns}px ${SLAB}`) > nameW) { ns = Math.max(28, Math.min(base.n, 46) - step * 2); nameL = wrap(k.name, nameW, `${ns}px ${SLAB}`); }
      const nameY = 70, nameH = Math.max(nameL.length * ns * 1.12, r * 2 - 40) + 18;
      const factsL = wrap(k.facts, textW, `${f}px ${TYPE}`), flh = f * 1.36;
      const holdL = wrap(k.holding, textW, `${hs}px ${HAND}`), hlh = hs * 1.12;
      const factsLbl = nameY + nameH + 20, factsTop = factsLbl + lbl + 14;
      const holdLbl = factsTop + factsL.length * flh + 26, holdTop = holdLbl + lbl + 12;
      const need = holdTop + holdL.length * hlh + (k.eoc ? 84 : 34);
      L = { X, W, r, left, textW, ns, nameL, nameY, nameH, f, factsL, flh, hs, holdL, hlh, lbl, factsLbl, factsTop, holdLbl, holdTop, need, step };
      if (need <= (big ? 1080 - 330 : lim)) break;
    }
    const minH = big ? 0 : BOTTOM - top0;
    L.h = Math.max(L.need, minH);
    L.top = big ? Math.max(40, Math.min(top0, 1076 - L.h)) : Math.max(minTop(), Math.min(top0, BOTTOM - L.h));
    L.sx = X + W - L.r * 1.3; L.sy = L.top + L.r * 1.25 + 8;
    return L;
  }

  /* the courtroom's fixed paper shapes */
  const chairPts = (h, key) => {
    const p = [], w = 66;
    p.push([-w / 2, 0], [-w / 2, -h + w / 2]);
    for (let i = 1; i < 12; i++) { const a = Math.PI + i / 12 * Math.PI; p.push([Math.cos(a) * w / 2, -h + w / 2 + Math.sin(a) * w / 2]); }
    p.push([w / 2, -h + w / 2], [w / 2, 0]);
    return Object.assign(p, { k: 'court:' + key });
  };
  const CHAIR = chairPts(112, 'chair'), CHAIR_C = chairPts(132, 'chairC');
  const SWAG = Object.assign((() => { const p = []; for (let i = 0; i <= 20; i++) { const u = i / 20; p.push([-520 + 1040 * u, Math.sin(u * Math.PI) * 46]); } p.push([520, -40], [-520, -40]); return p; })(), { k: 'court:swag' });

  class Court extends Scene {
    get n() { return this.cases ? this.cases.length : 4; }
    get of() { return this.n * 2; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 0.6)); }
    get cur() { return this.cases ? this.cases[this.case] : null; }
    get q() { const k = this.cur; return !k ? null : this.phase === 'idea' ? k.idea : this.phase === 'ruling' || this.phase === 'docket' ? k.ruling : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() { const k = this.cur; return k ? { ruling: k.ruling.answerIndex, idea: k.idea.answerIndex } : null; }
    answer() { return this.solution; }

    enter() {
      this.phase = 'intro'; this.cases = null; this.case = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map(); this.picks = { ruling: [], idea: [] }; this.ev = []; this.cues = {};
      Audio.play('paper', { gain: 0.5 });
      this.begin(false);
    }
    exit() { this.bankStamp(); }

    /* ---- the attempt (Rights.attempt: seeded, saved in Game.progress.bank.court) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || (Game.progress.bank = {}), prevSt = S[STEP], run = !fresh && prevSt && prevSt.open && prevSt.run ? prevSt.run : null;
      const a = Rights.attempt(STEP, json(), { fresh });
      this.cases = a.cases; this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false; this.lays.clear();
      if (fresh) { this.startCase(0); return; }
      if (a.resumed && run && this.restore(run)) return;
      this.phase = 'intro'; this.introAt = Game.time;
    }
    start() { if (this.phase === 'intro') { this.startCase(0); return true; } return false; }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.cases.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.phase = 'ruling'; this.sheetAt = Game.time - SLIDE - 1;
        this.quiet = true;
        for (const [k, v] of run.ev || []) if (k === 'c') this.choose(v);
        this.quiet = false;
        if (this.phase === 'reveal') { this.revealAt = Game.time - 20; this.cues = { tear: 1, seal: 1, gavel: 1, eoc: 1 }; }
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[court] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.case = i; this.ev = []; this.picks = { ruling: [], idea: [] }; this.cues = {};
      this.sheetAt = Game.time; this.qAt = Game.time + SLIDE * 0.6; this.pickedAt = null; this.revealAt = null; this.lastPick = null;
      this._qc = null;
    }
    startCase(i) {
      this.setup(i); this.phase = 'docket';
      this.save();
      this.play('paper', 'whoosh', { gain: 0.5 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end' && this.phase !== 'intro') S.run = { i: this.case, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    play(name, alt, o) { if (!this.quiet) sfx(name, alt, o); }

    /* ---- the two questions ---- */
    choose(i) {
      if (this.phase === 'docket') this.phase = 'ruling';                          // a pick while it slides in: it's landed
      if (this.phase !== 'ruling' && this.phase !== 'idea') return false;
      const step = this.phase, k = this.cur, q = k[step], picks = this.picks[step];
      if (!(i >= 0 && i < q.options.length) || picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!picks.length) Game.progress.answer(`${STEP}/${k.key}/${step}`, q.options[i]);   // first try only
      picks.push(i); this.pickedAt = Game.time; this.lastPick = { step, i };
      if (i === q.answerIndex) {
        this.play('ding', null, { gain: 0.6 });
        if (step === 'ruling') { this.phase = 'idea'; this.qAt = Game.time + 0.15; this.lastPick = null; }
        else this.toReveal();
      } else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      this._qc = null;
      this.save();
      return true;
    }
    toReveal() {
      const k = this.cur, rs = this.picks.ruling.length === 1 ? 1 : 0, is = this.picks.idea.length === 1 ? 1 : 0;
      this.phase = 'reveal'; this.revealAt = Game.time; this.cues = {};
      this.results[this.case] = { case: this.case, id: k.id, name: k.name, year: k.year, vote: k.vote, eoc: k.eoc,
        rulingStar: rs, ideaStar: is, stars: rs + is, wrong: { ruling: this.picks.ruling.length - 1, idea: this.picks.idea.length - 1 } };
    }
    next() {
      if (this.phase !== 'reveal' || Game.time - this.revealAt < NEXT_GUARD) return false;
      if (this.case < this.cases.length - 1) this.startCase(this.case + 1);
      else this.close();
      return true;
    }
    close() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) delete S.run;
      Rights.finish(STEP);                                                           // the next attempt draws new cases
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.cases) this.begin();
      if (this.phase !== 'end') this.close();
      this.collected = true; Game.complete(STEP);
    }
    finish() { if (this._finished) return; this._finished = true; this.done(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new cases)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new cases)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    /* the reveal's beats (sound cues fire once each; a resumed reveal is already over) */
    beats() {
      const R = this.revealAt, L = this.sheet();
      const hold = R + 1.15, eoc = hold + L.holdL.length * 0.45 + 0.2;
      return { tear: R + 0.05, name: R + 0.3, seal: R + 0.55, gavel: R + 0.95, holdLbl: R + 1.0, hold, eoc, card: R + 0.35 };
    }
    update() {
      if (this.phase === 'docket' && Game.time - this.sheetAt > SLIDE + 0.05) this.phase = 'ruling';
      if (this.phase === 'reveal' && !this.quiet) {
        const B = this.beats(), k = this.cur;
        const cue = (id, at, fn) => { if (!this.cues[id] && Game.time >= at) { this.cues[id] = 1; fn(); } };
        cue('tear', B.tear, () => sfx('tear', 'paper', { gain: 0.6 }));
        cue('seal', B.seal, () => sfx('stamp', 'thunk', { gain: 0.7 }));
        cue('gavel', B.gavel, () => sfx('gavel', 'thunk', { gain: 0.8, rate: 0.85 }));
        if (k.eoc) cue('eoc', B.eoc, () => sfx('pop', null, { gain: 0.5 }));
      }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.start(); }
      else if (this.phase === 'docket' || this.phase === 'ruling' || this.phase === 'idea') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'reveal') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- layout ---------------- */
    sheet() {
      const docked = UI.docked(), key = this.case + '|' + docked + '|' + Math.round(UI.text(24)) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { L = docked ? layoutSheet(this.cur, { X: 150, W: 1620, top0: 330, big: true }) : layoutSheet(this.cur, { X: 34, W: 980, top0: 338 }); this.lays.set(key, L); }
      return L;
    }
    /* the right-hand card: the question + options (+ the why of a wrong pick), or the verdict after the reveal */
    qCard() {
      const key = this.case + '|' + this.phase + '|' + this.picks.ruling.length + '|' + this.picks.idea.length + '|' + Math.round(UI.text(28));
      if (this._qc && this._qc.key === key) return this._qc;
      const X = 1050, W = 850, top0 = 338, lim = minTop(), k = this.cur;
      let R;
      for (let step = 0; step < 10; step++) {
        const f = 1 - step * 0.05;
        const ls = Math.round(UI.text(22) * f), qs = Math.round(UI.text(38) * f), os = Math.round(UI.text(28) * f), ws = Math.round(UI.text(30) * f), hs = Math.round(UI.text(40) * f), ss = Math.round(UI.text(34) * f);
        let rows = [], h = 26;
        const add = (kind, lines, size, lh, extra = 0) => { rows.push({ kind, lines, size, lh, y: h }); h += lines.length * lh + extra; };
        if (this.phase === 'reveal') {
          const r = this.results[this.case] || { stars: 0, rulingStar: 0, ideaStar: 0 };
          add('head', wrap(r.stars === 2 ? 'Well reasoned, Justice.' : r.stars === 1 ? 'Split decision: one of two.' : 'Overruled. Now you know.', W - 70, `${hs}px ${SLAB}`), hs, hs * 1.15, 12);
          add('label', ['THE RULING'], ls, ls * 1.4, 2);
          add('right', wrap(k.ruling.options[k.ruling.answerIndex], W - 70, `${os}px ${SERIF}`), os, os * 1.28, 14);
          add('label', ['THE IDEA THAT DECIDED IT'], ls, ls * 1.4, 2);
          add('right', wrap(k.idea.options[k.idea.answerIndex], W - 70, `${os}px ${SERIF}`), os, os * 1.28, 14);
          add('stars', [`+${r.stars} star${r.stars === 1 ? '' : 's'} (ruling ${r.rulingStar}, idea ${r.ideaStar})`], ss, ss * 1.25, 10);
        } else {
          const step2 = this.phase === 'idea', q = step2 ? k.idea : k.ruling, picks = this.picks[step2 ? 'idea' : 'ruling'];
          add('label', [`CASE No. ${this.case + 1} · QUESTION ${step2 ? 2 : 1} OF 2`], ls, ls * 1.4, 8);
          add('q', wrap(step2 ? Rights.IDEA_Q : Rights.RULING_Q, W - 70, `${qs}px ${SLAB}`), qs, qs * 1.15, 14);
          const opts = q.options.map(o => wrap(o, W - 140, `${os}px ${SERIF}`));
          opts.forEach((ls2, i) => { const rh = Math.max(UI.tap(84), ls2.length * os * 1.25 + 26); rows.push({ kind: 'opt', i, lines: ls2, size: os, lh: os * 1.25, y: h, rh }); h += rh + 8; });
          const last = picks[picks.length - 1];
          if (last != null && last !== q.answerIndex) { h += 8; add('why', wrap(q.explain, W - 70, `${ws}px ${HAND}`), ws, ws * 1.15, 0); }
        }
        h += 24;
        const bh = this.phase === 'reveal' ? UI.tap(96) + 24 : 0;
        R = { key, X, W, rows, h, bh, top: Math.max(lim, Math.min(top0, BOTTOM - bh - h)) };
        if (R.top + h + bh <= BOTTOM) break;
      }
      return (this._qc = R);
    }
    sub() {
      if (!this.cases || this.phase === 'intro' || this.phase === 'end') return '';
      return `case ${this.case + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }

    /* ---------------- drawing ---------------- */
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      if (this.phase === 'end') { UI.hud(TITLE, this.sub()); return this.drawEnd(docked); }
      this.drawRoom();
      UI.hud(TITLE, this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      const L = this.sheet();
      this.drawSheet(L);
      if (docked) return this.drawDock();
      if (this.phase === 'reveal') this.drawVerdict(); else this.drawQuestion();
    }

    /* the courtroom: marble wall, the red drape, the Court building at the back, nine empty chairs, the bench, the gavel */
    drawRoom() {
      const c = C(), t0 = this.enteredAt;
      paperRect(960, 150, 1960, 330, MARBLE, { seed: 1001, depth: 0.4, boil: false });
      g.save(); g.fillStyle = 'rgba(120,105,80,0.10)'; for (let x = 60; x < 1920; x += 150) g.fillRect(x, 0, 18, 300); g.restore();
      paperRect(960, 170, 1180, 300, DRAPE, { seed: 1002, depth: 0.8, boil: false });
      g.save(); g.fillStyle = 'rgba(40,6,6,0.22)'; for (let x = 390; x < 1540; x += 58) g.fillRect(x, 24, 14, 300); g.restore();
      paperPiece(SWAG, 960, 30, '#a8342c', { seed: 1003, depth: 0.7, boil: false });
      const rise = E.outBack(P(T, t0 + 0.1, t0 + 0.6), 1.3);
      CV().building('court', 960, 318 + (1 - rise) * 60, 250, { seed: 1004 });
      // nine empty chairs (the Chief Justice's in the middle, a little taller)
      for (let i = 0; i < 9; i++) {
        const x = 960 + (i - 4) * 124, s = pop(T, t0 + 0.25 + Math.abs(i - 4) * 0.05, 0.3, 2);
        if (s <= 0) continue;
        paperPiece(i === 4 ? CHAIR_C : CHAIR, x, 300, LEATHER, { seed: 1010 + (i % 4), depth: 0.9, s, boil: false });
        g.save(); g.fillStyle = 'rgba(214,176,96,0.75)';
        for (let j = 0; j < 5; j++) { g.beginPath(); g.arc(x - 26 + j * 13, 300 - (i === 4 ? 132 : 112) + 38, 2.6, 0, TAU); g.fill(); }
        g.restore();
      }
      // the bench
      paperRect(960, 318, 2000, 46, WOOD, { seed: 1020, depth: 1, boil: false });
      paperRect(960, 297, 2000, 8, WOOD_HI, { seed: 1021, depth: 0, boil: false });
      this.drawGavel();
    }
    drawGavel() {
      const gx = 1700, gy = 322, h = 176, B = this.phase === 'reveal' && this.revealAt != null ? this.beats() : null;
      let rot = 0;
      if (B) {
        const u = T - B.gavel;
        if (u > -0.28 && u < 0) rot = -0.22 * E.outCubic((u + 0.28) / 0.28);
        else if (u >= 0 && u < 0.35) rot = 0.05 * Math.sin(u * 40) * (1 - u / 0.35);
      }
      g.save(); g.translate(gx + 150, gy); g.rotate(rot); g.translate(-(gx + 150), -gy);
      if (IMG.gavel) cut('gavel', gx, gy, h, { ax: 0.5, ay: 1, seed: 1030, depth: 1 });
      else CV().tool('gavel', gx, gy - h / 2, h, { seed: 1030 });
      g.restore();
      if (B) {
        const u = T - B.gavel;
        if (u >= 0 && u < 0.4) {
          const pr = clamp(u / 0.12), a = 1 - clamp((u - 0.2) / 0.2);
          g.save(); g.globalAlpha *= a;
          for (let j = 0; j < 4; j++) { const an = -Math.PI * (0.62 + j * 0.12), r0 = 70, r1 = 70 + 50 * pr; ink([[gx - 70 + Math.cos(an) * r0, gy - 70 + Math.sin(an) * r0], [gx - 70 + Math.cos(an) * r1, gy - 70 + Math.sin(an) * r1]], { w: 5, color: INK, seed: 1040 + j, passes: 1, step: 20 }); }
          g.restore();
        }
      }
    }

    /* the docket sheet: slides in; the name under a flap until the reveal; seal, holding and EOC badge on the reveal */
    drawSheet(L) {
      const k = this.cur, c = C(), Cv = CV(), docked = UI.docked();
      const u = clamp((T - this.sheetAt) / SLIDE), dx = -(1 - E.outCubic(u)) * 1300, rot0 = (1 - u) * -0.05;
      const X = L.X, W = L.W, top = L.top, h = L.h, cx = X + W / 2, cy = top + h / 2;
      g.save(); g.translate(dx, 0); g.translate(cx, cy); g.rotate(rot0); g.translate(-cx, -cy);
      Cv.parchment(cx, cy, Math.round(W / 10) * 10, Math.round(h / 10) * 10, { seed: 1100 + this.case, depth: 1.4 });
      const lx = X + L.left;
      ink([[X + 46, top + 26], [X + 46, top + h - 26]], { w: 2, color: 'rgba(158,43,37,0.45)', seed: 1101, passes: 1, step: 60, amp: 0.4 });
      text(['DOCKET'], lx, top + 22, 34, `${Math.round(L.lbl * 1.2)}px ${TYPE}`, c.brick);
      text([`No. ${this.case + 1} of ${this.n}`], X + W - L.r * 2.7, top + 22, 34, `${L.lbl}px ${TYPE}`, c.brick, 'right');
      ink([[lx, top + 60], [X + W - L.r * 2.6, top + 60]], { w: 2.5, color: INK, seed: 1102, passes: 1, step: 60, amp: 0.4 });
      const B = this.phase === 'reveal' ? this.beats() : null;
      // the case name (under the flap until the reveal)
      const nTop = top + L.nameY, nMid = nTop + L.nameH / 2 - 9;
      if (B && T >= B.tear) text(L.nameL, lx, nMid - L.nameL.length * L.ns * 1.12 / 2, L.ns * 1.12, `${L.ns}px ${SLAB}`, INK);
      // the facts
      text(['THE FACTS'], lx, top + L.factsLbl, L.lbl * 1.3, `${L.lbl}px ${TYPE}`, c.brick);
      text(L.factsL, lx, top + L.factsTop, L.flh, `${L.f}px ${TYPE}`, INK);
      // the holding
      if (B && T >= B.holdLbl) {
        text(['THE COURT HELD'], lx, top + L.holdLbl, L.lbl * 1.3, `${L.lbl}px ${TYPE}`, c.brick);
        L.holdL.forEach((l, j) => hand(l, lx, top + L.holdTop + (j + 0.5) * L.hlh, L.hs, B.hold + j * 0.45, { align: 'left', color: c.navy, seed: 1110 + j, dur: 0.42 }));
      } else if (!B) {
        text(['OPINION PENDING …'], lx, top + L.holdLbl, L.lbl * 1.3, `${L.lbl}px ${TYPE}`, 'rgba(91,100,117,0.55)');
      }
      // the seal's empty ring before the reveal
      if (!B || T < B.seal) {
        inkCircle(L.sx, L.sy, L.r * 0.9, { w: 3, color: 'rgba(91,100,117,0.45)', seed: 1120, passes: 1 });
        text(['SEAL'], L.sx, L.sy - L.lbl * 0.7, L.lbl * 1.4, `${L.lbl}px ${TYPE}`, 'rgba(91,100,117,0.6)', 'center');
      }
      g.restore();
      // after the slide-in everything below is in stage space (the sheet no longer moves)
      if (!B) { this.drawFlap(L, 1, 0); return; }
      if (T < B.tear + 0.7) this.drawFlap(L, 1, clamp((T - B.tear) / 0.7));
      if (T >= B.seal) Cv.seal(L.sx, L.sy, L.r, { text: String(k.year), sub: dash(k.vote), seed: 1130, s: pop(T, B.seal, 0.3, 2.4), rot: -0.12, ribbon: true });
      if (k.eoc && T >= B.eoc) {
        const s = pop(T, B.eoc, 0.3, 2.2), bw = docked ? 520 : 380, bhh = docked ? 70 : 52;
        g.save(); g.translate(X + W - bw / 2 - 40, top + h - bhh / 2 - 22); g.rotate(-0.05); g.scale(s, s);
        paperRect(0, 0, bw, bhh, c.navy, { seed: 1140, depth: 0.8, boil: false });
        g.font = `${docked ? 36 : 26}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.cream; g.fillText('ON THE CIVICS EOC', 0, 2);
        g.restore();
      }
    }
    /* the paper flap over the case name: "CASE No. n"; on the reveal it tears away up and to the right */
    drawFlap(L, alpha, tear) {
      if (tear >= 1) return;
      const c = C(), u = clamp((T - this.sheetAt) / SLIDE), dxs = -(1 - E.outCubic(u)) * 1300;
      const w = Math.round((L.W - L.left - L.r * 2.6 - 16) / 10) * 10, h = Math.round(L.nameH / 2) * 2;
      const x = L.X + L.left - 14 + w / 2, y = L.top + L.nameY + L.nameH / 2 - 9;
      const e = E.inCubic ? E.inCubic(tear) : tear * tear * tear;
      const tx = dxs + e * 900, ty = -e * 520 + tear * 60, rot = -0.02 + e * 0.9;
      g.save(); g.globalAlpha *= alpha * (1 - clamp((tear - 0.7) / 0.3));
      g.translate(x + tx, y + ty); g.rotate(rot);
      paperRect(0, 0, w, h, c.kraft, { seed: 1150, depth: 1.2, torn: true, tex: 'tex_kraft', boil: false });
      const fs = Math.round(Math.min(h * 0.46, UI.docked() ? 76 : 56));
      g.font = `${fs}px ${SLAB}`; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = c.navy;
      g.fillText(`CASE No. ${this.case + 1}`, -w / 2 + 28, -fs * 0.18);
      g.font = `${Math.round(fs * 0.42)}px ${TYPE}`; g.fillStyle = INK;
      g.fillText('name sealed until you rule', -w / 2 + 30, fs * 0.52);
      g.restore();
    }

    /* the question card (desktop / landscape) */
    drawCard(x, y, w, h, seed) { CV().parchment(x + w / 2, y + h / 2, Math.round(w / 10) * 10, Math.round(h / 10) * 10, { seed, depth: 1.3 }); }
    drawQuestion() {
      if (T < this.qAt) return;
      const R = this.qCard(), { X, W, rows, h, top } = R, c = C(), step = this.phase === 'idea' ? 'idea' : 'ruling', q = this.cur[step], picks = this.picks[step];
      const s = pop(T, this.qAt, 0.25, 1.4);
      g.save(); g.translate(X + W / 2, top + h / 2); g.scale(s, s); g.translate(-(X + W / 2), -(top + h / 2));
      this.drawCard(X, top, W, h, 1200 + (step === 'idea' ? 1 : 0));
      rows.forEach(r => {
        const y = top + r.y;
        if (r.kind === 'label') text(r.lines, X + 36, y, r.lh, `${r.size}px ${TYPE}`, c.brick);
        else if (r.kind === 'q') text(r.lines, X + 36, y, r.lh, `${r.size}px ${SLAB}`, c.navy);
        else if (r.kind === 'why') r.lines.forEach((l, j) => hand(l, X + 36, y + (j + 0.5) * r.lh, r.size, this.pickedAt + 0.15 + j * 0.2, { align: 'left', color: c.brick, seed: 1210 + j }));
        else if (r.kind === 'opt') {
          const i = r.i, rh = r.rh, cyy = y + rh / 2, crossed = picks.includes(i);
          const hit = crossed || s < 0.98 ? { hover: false, clicked: false } : UI._hit(`ct-opt${this.case}${step}_${i}`, X + W / 2, cyy, W - 30, rh - 4);
          paperRect(X + W / 2, cyy, Math.round((W - 44) / 10) * 10, Math.round((rh - 8) / 2) * 2, hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : PAL.cream, { seed: 1220 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
          g.save(); g.fillStyle = crossed ? PENCIL : c.navy; g.fillRect(X + 36, cyy - 22, 44, 44);
          g.font = `26px ${SLAB}`; g.fillStyle = c.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), X + 58, cyy + 1); g.restore();
          text(r.lines, X + 98, cyy - r.lines.length * r.lh / 2, r.lh, `${r.size}px ${SERIF}`, crossed ? PENCIL : INK);
          if (crossed) {
            // strike every line of the option
            const a = this.lastPick && this.lastPick.step === step && this.lastPick.i === i ? this.pickedAt : -1e9, y0 = cyy - r.lines.length * r.lh / 2;
            r.lines.forEach((l, j) => {
              const mw = measure(l, `${r.size}px ${SERIF}`), ly = y0 + (j + 0.5) * r.lh;
              ink([[X + 92, ly + 6], [X + 106 + mw, ly - 6]], { w: 5, color: c.brick, seed: 1230 + i * 5 + j, prog: clamp((T - a - j * 0.15) / 0.3), passes: 1 });
            });
          }
          if (hit.clicked) this.choose(i);
        }
      });
      g.restore();
    }
    /* the verdict card after the reveal: the right ruling and idea, the stars, "Next case" */
    drawVerdict() {
      const B = this.beats();
      if (T < B.card) return;
      const R = this.qCard(), { X, W, rows, h, top } = R, c = C();
      const s = pop(T, B.card, 0.3, 1.5);
      g.save(); g.translate(X + W / 2, top + h / 2); g.scale(s, s); g.translate(-(X + W / 2), -(top + h / 2));
      this.drawCard(X, top, W, h, 1250);
      rows.forEach(r => {
        const y = top + r.y;
        if (r.kind === 'head') text(r.lines, X + 36, y, r.lh, `${r.size}px ${SLAB}`, c.brick);
        else if (r.kind === 'label') text(r.lines, X + 36, y, r.lh, `${r.size}px ${TYPE}`, c.brick);
        else if (r.kind === 'right') text(r.lines, X + 36, y, r.lh, `${r.size}px ${SERIF}`, INK);
        else if (r.kind === 'stars') hand(r.lines[0], X + 36, y + r.lh / 2, r.size, B.card + 0.4, { align: 'left', color: c.navy, seed: 1260, dur: 0.4 });
      });
      g.restore();
      const bh = UI.tap(96), label = this.case < this.n - 1 ? 'Next case ▶' : 'See my stars ▶';
      if (Game.time - this.revealAt > NEXT_GUARD + 0.4 && UI.button('ct-next', label, X + W / 2, top + h + 24 + bh / 2, { w: 440, h: 96, color: c.brick, size: 46 })) this.next();
    }

    /* ---- portrait: the room + sheet are the picture; everything to read and tap is in the dock ---- */
    drawDock() {
      const k = this.cur, id = this.attempt + '_' + this.case;
      if (this.phase !== 'reveal') {
        const step = this.phase === 'idea' ? 'idea' : 'ruling', q = k[step], picks = this.picks[step];
        Dock.heading('ct-case' + id, `Case No. ${this.case + 1} of ${this.n}: the facts`);
        Dock.text('ct-facts' + id, [k.facts], { cls: 'note' });
        if (step === 'idea') Dock.text('ct-ruled' + id, ['Ruling: ' + k.ruling.options[k.ruling.answerIndex]], { cls: 'right' });
        const st = q.options.map((o, i) => (picks.includes(i) ? 'wrong' : ''));
        // the why of a wrong pick as its own line (the collage dock has no style for per-option notes)
        Dock.choices('ct-' + step + id, { prompt: step === 'idea' ? Rights.IDEA_Q : Rights.RULING_Q, options: q.options, state: st, onChoose: i => this.choose(i) });
        if (picks.length && picks[picks.length - 1] !== q.answerIndex) Dock.text('ct-why' + step + id + '_' + picks.length, ['Not quite: ' + q.explain], { cls: 'wrong', reveal: true });
        return;
      }
      const r = this.results[this.case] || { stars: 0, rulingStar: 0, ideaStar: 0 };
      Dock.heading('ct-name' + id, k.name, { cls: 'big' });
      Dock.text('ct-seal' + id, [`Decided ${k.year}, by a vote of ${dash(k.vote)}.` + (k.eoc ? ' On the Florida Civics EOC list.' : '')], { cls: 'note' });
      Dock.text('ct-hold' + id, ['The Court held: ' + k.holding], { cls: 'note', reveal: true });
      Dock.text('ct-stars' + id, [`+${r.stars} star${r.stars === 1 ? '' : 's'} (ruling ${r.rulingStar}, idea ${r.ideaStar})`], { cls: 'right', reveal: true });
      Dock.choices('ct-rv-r' + id, { prompt: Rights.RULING_Q, options: k.ruling.options, state: k.ruling.options.map((o, i) => (i === k.ruling.answerIndex ? 'right' : this.picks.ruling.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.choices('ct-rv-i' + id, { prompt: Rights.IDEA_Q, options: k.idea.options, state: k.idea.options.map((o, i) => (i === k.idea.answerIndex ? 'right' : this.picks.idea.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.button({ id: 'next', glyph: this.case < this.n - 1 ? 'Next case ▶' : 'See my stars ▶', label: 'next', aria: 'next case', go: true, onClick: () => this.next() });
    }

    introLines() {
      return ['Four real Supreme Court cases. The names are sealed.', 'Read the facts. How did the Court rule?',
        'Then: which idea decided it? The seal shows the real year and vote.', `${this.n} cases. ${this.passStars} of ${this.of} stars to put your opinions on the record.`];
    }
    drawJudge(x, y, h, facing = 1) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('judge', x, y + (1 - rise) * 500, h, { facing, t: T, env: 0 });
    }
    drawIntro(docked) {
      const t0 = this.enteredAt, c = C(), Cv = CV(), lines = this.introLines();
      if (docked) {
        this.drawJudge(1640, 1080, 700, -1);
        Cv.woodtype('YOU BE THE JUSTICE', 800, 600, 90, { color: c.cream, ink: c.navy, t0: t0 + 0.2, seed: 1300 });
        Dock.heading('ct-intro', TITLE, { cls: 'big' });
        Dock.text('ct-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Open the first case', go: true, color: 'red', onClick: () => this.start() });
        return;
      }
      this.drawJudge(300, 1080, 700, 1);
      Cv.woodtype('YOU BE THE JUSTICE', 1160, 430, 78, { color: c.cream, ink: c.navy, t0: t0 + 0.2, seed: 1300 });
      const fs = Math.round(UI.text(36)), W = 1220;
      const wl = lines.map(l => wrap(l, W - 70, `${fs}px ${SERIF}`));
      const ch = wl.reduce((a, l) => a + l.length, 0) * fs * 1.42 + 50, bh = UI.tap(110);
      const top = Math.max(500, Math.min(520, BOTTOM - bh - 30 - ch));
      this.drawCard(1160 - W / 2, top, W, ch, 1310);
      let y = top + 25;
      wl.forEach((ls, i) => ls.forEach(tl => {
        if (T >= t0 + 0.5 + i * 0.3) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? c.brick : INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.3) / 0.3); g.fillText(tl, 1160, y + fs * 0.71); g.restore(); }
        y += fs * 1.42;
      }));
      if (this.t > 0.8 && UI.button('ct-start', 'Start ▶', 1160, Math.min(BOTTOM - bh / 2, top + ch + 26 + bh / 2), { w: 420, h: 110, color: c.brick, size: 56 })) this.start();
    }

    /* the end: the four dockets (seal = the real year and vote), the stars, the buttons */
    drawDockets(cx0, cy0, w, h, gx, gy) {
      const Cv = CV(), c = C();
      this.cases.forEach((k, i) => {
        const x = cx0 + (i % 2) * gx, y = cy0 + Math.floor(i / 2) * gy, r = this.results[i] || { stars: 0 };
        Cv.docket(x, y, w, h, { name: k.name, year: k.year, vote: k.vote, eoc: k.eoc, seed: 1400 + i * 7, rot: [-0.01, 0.012, 0.008, -0.012][i],
          t0: this.endAt + 0.3 + i * 0.15, lines: [k.case ? k.case.idea : ''] });
        hand(`${r.stars} of 2 ★`, x - w / 2 + 70, y + h / 2 - 44, Math.round(h * 0.17), this.endAt + 0.6 + i * 0.12, { align: 'left', color: r.stars === 2 ? c.navy : c.brick, seed: 1420 + i, rot: -0.03 });
      });
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons(), c = C(), Cv = CV();
      const msg = this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New cases?`;
      if (docked) {
        Cv.woodtype(`${s} / ${of}`, 960, 150, 110, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 1450 });
        this.drawDockets(500, 450, 860, 330, 920, 390);
        UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 1500, 150, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.0, color: c.brick });
        Dock.heading('ct-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('ct-end', [msg]);
        Dock.list('ct-end', this.cases.map((k, i) => { const r = this.results[i] || { stars: 0 }; return { badge: String(i + 1), color: r.stars === 2 ? c.navy : c.brick, lines: [`${k.name} (${k.year}, ${dash(k.vote)})`, `${r.stars} of 2 stars`] }; }));
        Dock.text('ct-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('ct-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Cv.woodtype(`${s} / ${of}`, 1030, 118, 96, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 1450 });
      UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 1500, 122, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 0.7, color: c.brick });
      this.drawDockets(505, 365, 850, 280, 910, 310);
      const bh = UI.tap(100), by = BOTTOM - 14 - bh / 2;
      hand(msg, 960, by - bh / 2 - 44, Math.round(UI.text(40)), this.endAt + 0.8, { color: c.brick, seed: 1460, dur: 0.5 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('ct-end' + i, b.label, 700 + i * 520, by, { w: 480, h: 100, color: i === 0 ? c.brick : c.navy, size: 44 })) b.go(); });
      hand(`quiz code ${this.seed}`, 1890, by + 20, 28, this.endAt + 0.6, { align: 'right', color: PAL.ink, seed: 1465 });
    }
  }

  Court.STEP = STEP;
  Object.defineProperty(Court, 'solution', { get() { const s = Game.current(); return s instanceof Court ? s.solution : null; } });
  /* tests / screenshots: swap the attempt's cases for these item ids (the model builds them: CASES year + vote) */
  Court.preview = (ids, seed = 1) => {
    const s = Game.current(); if (!(s instanceof Court) || !s.cases) return false;
    const items = json().items, r = Bank.rng(seed);
    ids.forEach((id, i) => { const it = items.find(x => x.id === id); if (it && i < s.cases.length) s.cases[i] = Rights.courtCase(it, r); });
    s.lays.clear(); s._qc = null;
    return true;
  };
  Levels.court = Court;
})();
